/**
 * Formatação e mensagens da cotação de deslocamento (PoolTrack, 04/10).
 */
import {
  QUOTE_LEG_LABELS,
  QUOTE_POINT_LABELS,
  type QuoteLegKey,
  type QuotePointKey,
} from '@erp/shared';
import { errorMessage as billingErrorMessage } from '@/features/billing/billing-queries';

/** "11.1" → "11,1 km" (sempre uma casa) */
export function formatKm(km: string): string {
  const n = Number(km);
  if (!Number.isFinite(n)) return `${km} km`;
  return `${n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** 900 → "15 min"; 3900 → "1 h 05 min" */
export function formatDuration(seconds: number): string {
  const min = Math.round(seconds / 60);
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min`;
}

const MAPS_FAILURE_LABELS: Record<string, string> = {
  denied: 'o provedor de mapas recusou a chave (ou a API não está habilitada)',
  quota: 'a cota do provedor de mapas acabou por agora',
  invalid_request: 'o provedor de mapas recusou o pedido',
  unavailable: 'o provedor de mapas está fora do ar',
  timeout: 'o provedor de mapas não respondeu a tempo',
  network: 'não foi possível falar com o provedor de mapas',
  bad_response: 'o provedor de mapas respondeu fora do formato',
};

const ERROR_LABELS: Record<string, string> = {
  maps_unavailable: 'Cálculo de km indisponível — informe o km de cada perna à mão, com o motivo.',
  route_receipt_invalid:
    'O cálculo desta cotação venceu ou não vale para este ticket — clique em Calcular de novo.',
  route_stale: 'Um ponto mudou depois do cálculo — clique em Calcular de novo.',
  quote_total_zero:
    'O total está zerado — informe o preço por km de pelo menos uma perna, ou o valor cheio no total.',
  quote_price_required:
    'Falta o preço por km de uma perna — informe-o, ou digite o valor cheio na linha do total.',
  quotes_unavailable: 'Cotações indisponíveis no momento — tente de novo.',
};

const listOf = (items: string[]) =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;

const pointNames = (points: QuotePointKey[]) =>
  listOf(points.map((p) => QUOTE_POINT_LABELS[p]?.toLowerCase() ?? p));

/** marcador solto fora do Brasil: volta para onde estava, sem consultar o provedor */
export const QUOTE_OUTSIDE_BRAZIL_MESSAGE =
  'Esse ponto fica fora do Brasil — o marcador voltou para onde estava.';

/** mensagem pt-BR de um erro da cotação; os códigos do billing e os transversais caem no fallback */
export function quoteErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'error' in err) {
    const { error: code, details } = err as { error: unknown; details?: unknown };
    const d = (details ?? {}) as {
      kind?: string;
      points?: QuotePointKey[];
      outsideBrazil?: QuotePointKey[];
      legs?: QuoteLegKey[];
    };
    if (code === 'maps_error') {
      return `Não deu para calcular: ${MAPS_FAILURE_LABELS[d.kind ?? ''] ?? 'falha no provedor de mapas'}. Tente de novo ou informe o km à mão.`;
    }
    if (code === 'address_not_found') {
      // `outsideBrazil`: o texto casou, mas só com lugar fora do Brasil
      const abroad = d.outsideBrazil ?? [];
      const missing = (d.points ?? []).filter((p) => !abroad.includes(p));
      const parts = [
        abroad.length > 0
          ? `Endereço não encontrado no Brasil: ${pointNames(abroad)} (o texto casou com um lugar fora do país).`
          : null,
        missing.length > 0 || abroad.length === 0
          ? `Endereço não encontrado: ${pointNames(missing) || 'confira os campos'}.`
          : null,
      ];
      return `${parts.filter(Boolean).join(' ')} Acrescente número, cidade e UF.`;
    }
    if (code === 'point_outside_brazil') {
      return `Esse ponto fica fora do Brasil: ${pointNames(d.points ?? []) || 'confira o mapa'}. A cotação só calcula dentro do Brasil.`;
    }
    if (code === 'route_not_found') {
      const names = (d.legs ?? []).map((l) => QUOTE_LEG_LABELS[l]?.toLowerCase() ?? l);
      return `Não há rota de carro para a ${listOf(names) || 'perna'} — confira os pontos ou informe o km à mão.`;
    }
    if (typeof code === 'string' && ERROR_LABELS[code]) return ERROR_LABELS[code];
  }
  return billingErrorMessage(err);
}
